'use client';

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useI18n } from 'next-localization';

import { every, isEmpty } from 'lodash-es';

import { FormError } from '@/components/form/form-error';
import { ALL_FACILITY_OPTION } from '@/features/listing/constants';
import { useListingContext } from '@/features/listing/hooks/use-listing-context';
import { ListingData } from '@/features/listing/listing.types';
import { useBasePath } from '@/hooks/use-base-path';
import { useCustomRouting } from '@/hooks/use-custom-routing';
import { useTermAutoComplete } from '@/hooks/use-term-auto-complete';
import { CELEBRUS_TYPEAHEAD_ID } from '@/lib/constants';
import { cn, getPhysicianProfileUrl } from '@/lib/utils';
import { NeuChangeEvent, NeuSearch, NeuSearchProps } from '@hcahealthcaredpx/neutron-react';

import { BLUR_DEBOUNCE_MS, SEARCH_MIN_CHARS } from '../constants';
import useGenerateProfileUrl from '../hooks/use-generate-profile-url';
import styles from '../landing-search.module.css';
import {
  AutocompleteCategories,
  AutocompleteCategoryList,
  AutocompleteQueries,
  AutocompleteSearchRequest,
  ProviderSearchFields,
  ProvidersInputRef,
} from '../landing-search.types';
import DropdownContent from './dropdown-content';
import { isSearchValid } from './search-bar';

type ProvidersSearchProps = Pick<ProviderSearchFields, 'coids' | 'program' | 'locationUrlTitle'> &
  NeuSearchProps & {
    setValid?: (isDisabled: boolean) => void;
    inputValue: ListingData;
    setInputValue: React.Dispatch<React.SetStateAction<ListingData>>;
    inputReset: () => void;
  };

export const ProvidersSearch = forwardRef<ProvidersInputRef, ProvidersSearchProps>(
  (
    {
      setValid,
      inputValue,
      setInputValue,
      inputReset,
      coids,
      locationUrlTitle,
      program = 'web',
      ...rest
    },
    inputRef
  ): JSX.Element => {
    const searchRef = useRef<HTMLInputElement>(null);
    const isProcessingSelectionRef = useRef(false);
    const pendingSelectionValueRef = useRef<string | null>(null);
    const [search, setSearch] = useState(inputValue?.label || '');
    const [providerName, setProviderName] = useState('');
    const { routerPush } = useCustomRouting();
    const { withBasePath } = useBasePath();
    const { setSelectedFacility } = useListingContext();

    const params: AutocompleteSearchRequest = {
      input: searchRef?.current?.value || '',
      coid: coids,
      program,
    };

    if (locationUrlTitle) {
      params.LocationUrlTitle = locationUrlTitle;
    }

    const { isLoading, data: response } = useTermAutoComplete(params);

    useImperativeHandle(inputRef, () => {
      return {
        clear() {
          if (searchRef && searchRef.current) {
            searchRef.current.value = '';
          }
        },
      };
    }, []);

    useEffect(() => {
      if (!isEmpty(inputValue?.value) && !search) {
        setValid?.(true);
      }
    }, [inputValue, setValid, search]);

    // Sync search state with inputValue when it changes (e.g., navigation from listing)
    useEffect(() => {
      if (inputValue?.label && inputValue.label !== search) {
        setSearch(inputValue.label);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue?.label]); // Keep search out of deps to avoid update loops

    const handleOnChange = useCallback(
      (e: NeuChangeEvent) => {
        // Todo: check if value exists in context, /maybe remove it from here/
        if (e.value.length === 0) {
          inputReset();
        }

        // if delete is pressed, set button to disabled
        if (search.length >= SEARCH_MIN_CHARS && search !== e.value) {
          setValid?.(false);
          inputReset();
        }
        setSearch(e.value);
      },
      [inputReset, search, setValid]
    );

    const { mutate: generateProfileMutation } = useGenerateProfileUrl({
      onSuccess: (response) => {
        const url = withBasePath(getPhysicianProfileUrl(response?.data));
        routerPush(url);
      },
    });

    const handleItemMouseDown = useCallback((listItem: AutocompleteCategoryList[number]) => {
      const selectedListItemValue = listItem.value ?? '';
      if (!selectedListItemValue) return;

      // Update the input value BEFORE blur fires so comparison works
      if (searchRef.current) {
        searchRef.current.value = selectedListItemValue;
      }

      // Store the pending selection value and set flag BEFORE blur fires
      pendingSelectionValueRef.current = selectedListItemValue.toLowerCase().trim();
      isProcessingSelectionRef.current = true;
    }, []);

    const handleItemClick = useCallback(
      (listItem: AutocompleteCategoryList[number], category: string) => {
        const selectedListItemValue = listItem.value ?? '';
        if (!selectedListItemValue) return;

        if (searchRef.current) {
          searchRef.current.value = selectedListItemValue;
        }

        setSearch(listItem?.value);

        if (category === AutocompleteCategories.providers) {
          setProviderName(listItem.value);
          setValid?.(false);
          generateProfileMutation({
            coids,
            NPIs: listItem.reference,
            program,
          });
          return;
        }

        if (category === AutocompleteCategories.specialties) {
          setInputValue({
            type: AutocompleteQueries.specialtyId,
            label: listItem.value,
            value: listItem.reference,
          });
        }

        if (category === AutocompleteCategories.visibleTerms) {
          setInputValue({
            type: AutocompleteQueries.keywordExact,
            label: listItem.value,
            value: listItem.reference,
          });
        }

        if (category === AutocompleteCategories.locations) {
          setInputValue({
            type: AutocompleteQueries.locationUrlTitle,
            label: listItem.value,
            value: listItem.reference,
          });

          setSelectedFacility(ALL_FACILITY_OPTION.coid); // reset facility when location changes from Search 1
        }

        setValid?.(true);
      },
      [generateProfileMutation, coids, program, setInputValue, setValid, setSelectedFacility]
    );

    const hasNoData = useMemo(
      () => every(response?.data, isEmpty) && !isEmpty(response?.data),
      [response?.data]
    );
    const hasError = search && !isSearchValid(search || '');

    const hideDropdown = inputValue?.label === search || providerName === search;

    // Pre-compute normalized values for blur validation to avoid repeated string operations
    const normalizedInputLabel = useMemo(
      () => inputValue?.label?.toLowerCase().trim() || '',
      [inputValue?.label]
    );

    const normalizedProviderName = useMemo(
      () => providerName?.toLowerCase().trim() || '',
      [providerName]
    );

    const handleBlur = useCallback(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (event: any) => {
        if (!searchRef?.current) return;

        // Check if focus is moving to the dropdown
        const relatedTarget = event.relatedTarget as HTMLElement;
        const isMovingToDropdown =
          relatedTarget?.closest(`#${CELEBRUS_TYPEAHEAD_ID}-dropdown`) ||
          relatedTarget?.closest('.neu-search--dropdown');

        // Don't clear if focus is moving to dropdown (keyboard navigation)
        if (isMovingToDropdown) {
          return;
        }

        const currentValue = searchRef.current.value.toLowerCase();
        const trimmedCurrentValue = currentValue.trim();

        // Don't clear if user just selected an item (check against pending selection)
        if (isProcessingSelectionRef.current) {
          isProcessingSelectionRef.current = false;
          const pendingValue = pendingSelectionValueRef.current;
          pendingSelectionValueRef.current = null;

          // If there was a pending selection, trust it and keep the value
          if (pendingValue && trimmedCurrentValue === pendingValue) {
            return;
          }
        }

        // Clear field if input doesn't match a valid selected item
        // Use pre-computed normalized values to avoid repeated string operations
        const isValidValue =
          (normalizedInputLabel && trimmedCurrentValue === normalizedInputLabel) ||
          (normalizedProviderName && trimmedCurrentValue === normalizedProviderName);

        if (!isValidValue && trimmedCurrentValue !== '') {
          searchRef.current.value = '';
          setSearch('');
        }
      },
      [normalizedInputLabel, normalizedProviderName]
    );

    const { t } = useI18n();

    return (
      <div className={cn('d-flex flex-column w-100', styles.providersSearch)}>
        <NeuSearch
          minLength={SEARCH_MIN_CHARS}
          debounce={BLUR_DEBOUNCE_MS}
          dropdown
          required
          onNeuChange={handleOnChange}
          onNeuBlur={handleBlur}
          ref={searchRef}
          defaultValue={inputValue?.label}
          aria-label={inputValue?.label}
          id={CELEBRUS_TYPEAHEAD_ID}
          {...rest}
        >
          {!hasError && hasNoData && (
            <div className="h-auto p-1 neu-text--align-center">
              <span
                role="alert"
                aria-label="error"
                className="d-flex align-self-start justify-content-start"
              >
                {t('OAS.LandingPage.NoDataMessage')}
              </span>
            </div>
          )}
          {!hasError && !hideDropdown && (
            <DropdownContent
              isLoading={isLoading}
              items={response?.data}
              searchRef={searchRef}
              handleItemClick={handleItemClick}
              handleItemMouseDown={handleItemMouseDown}
            />
          )}
        </NeuSearch>
        {hasError && <FormError errorMessage={t('OAS.LandingPage.ErrorMessage')} />}
      </div>
    );
  }
);

ProvidersSearch.displayName = 'ProvidersSearch';
